import MessageHandler from '../MessageHandler';
import { TopicMessageSchema } from '../messageSchemas';
import { MESSAGE_TYPES, COMMAND_TOPICS, DATA_CHANNELS } from '../messageTypes';

/**
 * CommandHandler - Handles outgoing command messages to robots
 *
 * Features:
 * - Validates command structure
 * - Supports multiple command topics (cmd_vel, joy, keyboard)
 * - Optional throttling for high-frequency commands
 * - Error handling and retry logic
 * - Statistics tracking
 *
 * @example
 * const handler = new CommandHandler(dataChannelSendFn, eventBus);
 * handler.sendCommand('/cmd_vel', { linear: { x: 0.5 }, angular: { z: 0.2 } });
 */
class CommandHandler extends MessageHandler {
  /**
   * Create CommandHandler
   * @param {Function} sendFunction - Function to send data via channel (channelSend)
   * @param {EventBus} eventBus - EventBus instance
   * @param {Object} [options] - Additional options
   */
  constructor(sendFunction, eventBus, options = {}) {
    super('command', eventBus, {
      throttle: options.throttle || 0, // No throttle by default
      schema: TopicMessageSchema,
      trackChanges: false,
      enableDebug: options.enableDebug || false,
      logger: options.logger || console,
      maxRetries: options.maxRetries || 0,
      ...options
    });

    if (typeof sendFunction !== 'function') {
      throw new TypeError('sendFunction must be a function');
    }

    this.sendFunction = sendFunction;
    this.channelName = options.channelName || DATA_CHANNELS.CHAT;

    // Command statistics
    this.commandStats = {
      sent: 0,
      failed: 0,
      byTopic: {}
    };
  }

  /**
   * Send command to robot
   * @param {string} topic - Command topic (e.g., '/cmd_vel', '/joy')
   * @param {*} value - Command value
   * @param {Object} [options] - Send options
   * @returns {boolean} True if sent successfully
   */
  sendCommand(topic, value, options = {}) {
    const message = {
      topic,
      value,
      timestamp: Date.now(),
      ...options
    };

    return this.process(message);
  }

  /**
   * Handle validated command message
   * @param {Object} message - Validated command message
   * @override
   */
  handle(message) {
    const { topic, value } = message;

    // Create data channel message wrapper
    const channelMessage = {
      channel: this.channelName,
      data: JSON.stringify({ topic, value })
    };

    try {
      // Send via data channel
      this.sendFunction(this.channelName, JSON.stringify({ topic, value }));

      // Update statistics
      this.commandStats.sent++;
      this.commandStats.byTopic[topic] = (this.commandStats.byTopic[topic] || 0) + 1;

      // Publish sent event
      this.publish('command:sent', {
        topic,
        value,
        channel: this.channelName,
        timestamp: Date.now()
      });

      if (this.enableDebug) {
        this.logger.debug(`[CommandHandler] Sent command:`, { topic, value });
      }

      return true;
    } catch (error) {
      this.commandStats.failed++;
      this.logger.error(`[CommandHandler] Failed to send command:`, error);

      // Publish error event
      this.publish('command:error', {
        topic,
        value,
        error: error.message,
        timestamp: Date.now()
      });

      throw error; // Let MessageHandler's retry logic handle it
    }
  }

  /**
   * Send twist command (cmd_vel)
   * @param {Object} twist - Twist message { linear: {x, y, z}, angular: {x, y, z} }
   * @returns {boolean} True if sent successfully
   */
  sendTwist(twist) {
    return this.sendCommand(COMMAND_TOPICS.CMD_VEL, twist);
  }

  /**
   * Send joystick command
   * @param {Object} joy - Joystick message { axes: [], buttons: [] }
   * @returns {boolean} True if sent successfully
   */
  sendJoy(joy) {
    return this.sendCommand(COMMAND_TOPICS.JOY, joy);
  }

  /**
   * Send keyboard command
   * @param {Object} keyboard - Keyboard message
   * @returns {boolean} True if sent successfully
   */
  sendKeyboard(keyboard) {
    return this.sendCommand(COMMAND_TOPICS.KEYBOARD, keyboard);
  }

  /**
   * Get command statistics
   * @returns {Object} Command statistics
   */
  getCommandStats() {
    return {
      ...this.commandStats,
      successRate: this.commandStats.sent > 0
        ? ((this.commandStats.sent / (this.commandStats.sent + this.commandStats.failed)) * 100).toFixed(2)
        : 0
    };
  }

  /**
   * Reset command statistics
   */
  resetCommandStats() {
    this.commandStats = {
      sent: 0,
      failed: 0,
      byTopic: {}
    };
  }

  /**
   * Clear state and statistics
   * @override
   */
  clear() {
    super.clear();
    this.resetCommandStats();
  }
}

export default CommandHandler;
